/**
 * Fern client wrapper for the durable Sessions API (PR #4916).
 *
 * Mirrors `trace/api/client.ts`: all session/transcript/stream/interaction calls go
 * through the Fern-generated `@agentaai/api-client` via the workspace SDK singleton.
 * The host app initialises the singleton at boot; every entity shares it through
 * `getAgentaSdkClient()`.
 */
import {
    getLowPriorityMountsClient as getSdkLowPriorityMountsClient,
    getLowPrioritySessionsClient as getSdkLowPrioritySessionsClient,
    getMountsClient as getSdkMountsClient,
    getSessionsClient as getSdkSessionsClient,
} from "@agenta/sdk/resources"

/** The Fern `sessions` resource client (streams, transcripts, states, interactions). */
export function getSessionsClient() {
    return getSdkSessionsClient()
}

/** Same client with the `priority: "low"` fetch hint — for secondary session reads
 * (record-replay hydration, liveness polling) that must yield to the live conversation stream. */
export function getLowPrioritySessionsClient() {
    return getSdkLowPrioritySessionsClient()
}

/** The Fern `mounts` resource client (agent working-directory files). */
export function getMountsClient() {
    return getSdkMountsClient()
}

/** Same client with the `priority: "low"` fetch hint — for background mount listing. */
export function getLowPriorityMountsClient() {
    return getSdkLowPriorityMountsClient()
}

/**
 * Per-request options scoping a Fern call to a project (and optionally an application).
 *
 * STANDING RULE: project/app scope ALWAYS rides queryParams, never the body. `abortSignal`
 * is threaded so TanStack Query can cancel in-flight requests.
 *
 * `maxRetries` overrides Fern's transport-level retry (default 2). Pass a low value for calls
 * where a retry can't help — a timed-out object-store LIST just re-times-out and hammers the
 * backend — so a failure surfaces once and is handled gracefully instead of compounding.
 */
export function projectScopedRequest(
    projectId: string,
    appId?: string,
    abortSignal?: AbortSignal,
    maxRetries?: number,
) {
    const queryParams: Record<string, string> = {}
    if (projectId) queryParams.project_id = projectId
    if (appId) queryParams.application_id = appId
    const options: {
        queryParams: Record<string, string>
        abortSignal?: AbortSignal
        maxRetries?: number
    } = {queryParams, abortSignal}
    if (maxRetries !== undefined) options.maxRetries = maxRetries
    return options
}

/** True for a Fern error carrying an HTTP 409, the server's "I refuse" rather than "I failed".
 *
 * Fern raises `AgentaApiError` with a `statusCode`, so this reads that field rather than the
 * message. Anything without one is a transport or unknown failure and must not be mistaken
 * for a refusal: the two call for opposite handling at a call site that shows an optimistic
 * value. */
export function isConflictError(error: unknown): boolean {
    return (
        typeof error === "object" &&
        error !== null &&
        (error as {statusCode?: unknown}).statusCode === 409
    )
}

/** True for fetch/Fern abort + timeout cancellations (vs real failures).
 *
 * Fern does NOT rethrow the raw `AbortError`: its fetcher repackages an aborted request as
 * an `AgentaApiError` (name `"AgentaApiError"`, message `"The user aborted a request"`) with
 * the original `DOMException` stashed on `cause`. So we unwrap the `cause` chain and also
 * match Fern's abort marker message — otherwise cancelled queries log as real failures. */
export function isAbortError(error: unknown): boolean {
    let current: unknown = error
    for (let depth = 0; current != null && depth < 5; depth++) {
        if (
            current instanceof DOMException &&
            (current.name === "AbortError" || current.name === "TimeoutError")
        ) {
            return true
        }
        if (typeof current === "object" && current !== null) {
            const name = (current as {name?: string}).name
            const message = (current as {message?: string}).message
            if (name === "AbortError" || message === "The user aborted a request") {
                return true
            }
            current = (current as {cause?: unknown}).cause
        } else {
            break
        }
    }
    return false
}

/** True when the request never reached the server (offline, API down, CORS): Fern wraps
 * fetch's `TypeError` as a status-less `AgentaApiError` with it on `cause`. A bare
 * `TypeError` thrown by the callback itself is a bug, not a network failure. */
export function isNetworkError(error: unknown): boolean {
    if (typeof error !== "object" || error === null || error instanceof TypeError) return false
    let current: unknown = error
    for (let depth = 0; current != null && depth < 5; depth++) {
        if (current instanceof TypeError) return true
        if (typeof current !== "object") break
        if ((current as {statusCode?: unknown}).statusCode !== undefined) return false
        current = (current as {cause?: unknown}).cause
    }
    return false
}

/**
 * Boundary wrapper for Fern calls. Fern throws `AgentaApiError` on non-2xx; we return
 * null on failure (logged) and rethrow aborts so query clients cancel cleanly.
 *
 * `isExpected` marks a status that is an answer, not a failure: null, unlogged.
 *
 * A network failure returns null unlogged too: the browser already reports it, and polled
 * reads would otherwise log it on every tick until the connection comes back.
 */
export async function callFern<T>(
    label: string,
    fn: () => Promise<T>,
    isExpected?: (error: unknown) => boolean,
): Promise<T | null> {
    try {
        return await fn()
    } catch (error) {
        if (isAbortError(error)) throw error
        if (isExpected?.(error)) return null
        if (isNetworkError(error)) return null
        console.error(`${label} failed:`, error instanceof Error ? error.message : String(error))
        return null
    }
}
